import { getSessionCookieData, isGokollab, isPwa } from '@ghl/clientportal-core/init'
import { computed, reactive } from 'vue'

import { redirectToNoCommunitiesPage } from '@/components/helper'
import config from '@/config'
import { LocationNotFoundError } from '@/error/locationNotFoundError'
import { i18n } from '@/main'
import { MemberStatus } from '@/models/Groups'
import { initializeGroup } from '@/service'
import { GroupsService } from '@/service/GroupsService'
import { UsersService } from '@/service/UsersService'
import restAgent from '@/service/restAgent'
import { useGlobalStore } from '@/store/app'
import { updateColorTokens } from '@/util/colorTokens'
import { getSessionLocationId } from '@ghl/clientportal-core'
import { GroupState } from './groups'
import { UserState } from './users'

class NoGroupsFoundError extends Error {
  constructor(message) {
    super(message)
    this.name = 'NoGroupsFoundError'
  }
}

class App {
  _recentGroupSlug: string | null = null

  state = reactive({
    locationId: null,
    windowGradient: true,
    fetching: true,
    isPwa: true,
    isInitializationStatus: false,
    locationData: {} as any,
    isMobile: window.innerWidth <= 930,
    postContainer: null as any,
    learningContainer: null as any,
    memberListContainer: null as any,
    leaderBoardContainer: null as any,
    showGroupSettings: false,
    showInviteModal: false,
    memberSettingActiveTab: 'GroupInvitations',
    // keeping this is app state, same can be used to implement global search later on
    globalSearchText: null as string | null,
    groupSettingsActiveTab: 'GroupDetails',
    readingMode: window.innerWidth <= 1400,
    container: computed(() => {
      return this.state.postContainer
    }),
  })

  constructor() {
    this.handleResize = this.handleResize.bind(this)
    this.addWindowResizeListener()
  }

  handleResize() {
    this.state.isMobile = window.innerWidth <= 930
    this.state.readingMode = window.innerWidth <= 1330 && GroupState.state?.userStatus === MemberStatus.ACTIVE
  }

  addWindowResizeListener() {
    window.addEventListener('resize', this.handleResize)
  }

  removeWindowResizeListener() {
    window.removeEventListener('resize', this.handleResize)
  }

  async toggleReadingMode() {
    this.state.readingMode = !this.state.readingMode
  }
  openGroupSettings = (defaultTab?: string) => {
    if (defaultTab) {
      this.state.groupSettingsActiveTab = defaultTab
    }
    this.state.showGroupSettings = true
  }

  openInvitationModal = () => {
    this.state.showInviteModal = true
  }

  async createApp(route, router) {
    try {
      this.state.fetching = true
      this.state.isPwa = isPwa()
      await this.fetchLocationDetails()

      const appStore = useGlobalStore()
      if (this.state.locationId) {
        appStore.setLocationId(this.state.locationId)
      }

      const promises = [this.fetchUserDetails(), this.fetchGroupDetails(route, router)]
      await Promise.all(promises)

      this.state.isInitializationStatus = true
      this.state.globalSearchText = null
      this.state.fetching = false
    } catch (error: any) {
      console.error(error)
      if (error instanceof NoGroupsFoundError) {
        router.push({ name: 'no-communities' })
      } else if (error instanceof LocationNotFoundError) {
        router.push({ name: 'not-found' })
      } else {
        throw error
      }
    } finally {
      this.state.fetching = false
      const globalStore = useGlobalStore()
      globalStore.setAppLoaded(true)
    }
  }

  async createAppForLocation() {
    try {
      this.state.fetching = true
      this.state.isPwa = isPwa()
      await this.fetchLocationDetails()
      await this.fetchUserDetails()
      const appStore = useGlobalStore()
      if (this.state.locationId) {
        appStore.setLocationId(this.state.locationId)
      }
    } catch (error: any) {
      console.error(error)
    } finally {
      this.state.fetching = false
      const globalStore = useGlobalStore()
      globalStore.setAppLoaded(true)
    }
  }

  async fetchLocationDetails() {
    try {
      const page: any = JSON.parse(localStorage.getItem('event') as any)
      let domain: string = page?.domain || window.location.hostname
      if (domain === 'localhost') {
        const urlParams = new URLSearchParams(window.location.search)
        const domainname = urlParams.get('domain')
        domain = domainname || 'communities.staging.clientclub.net'
      }

      this.state.locationData = isGokollab() ? await this.getGokollabLocationDetails() : await this.getNonGokollabLocationDetails(domain)

      useGlobalStore().setPermissions(this.state.locationData)
      const languagePreference = this.state.locationData?.languagePreferenceBuilder
      useGlobalStore().setLanguagePreference(languagePreference)

      // Apply location-based language preference even if user is not logged in
      // If user logs in later, their preference will override this in getCurrentUser()
      if (languagePreference) {
        i18n.global.locale.value = languagePreference
      }

      useGlobalStore().setChatWidgetPermissions(this.state.locationData)
      useGlobalStore().setChatWidgetId(this.state?.locationData?.chatWidgetSettings?.widgetId)
      useGlobalStore().setLocationId(this.state.locationData?.locationId)
      this.state.locationId = this.state.locationData?.locationId
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async getNonGokollabLocationDetails(domain: string) {
    try {
      if (isPwa()) {
        const locationId = getSessionLocationId()
        if (locationId) {
          return await this.getNonGokollabLocationDetailsByLocationId(locationId)
        }
      }
      return await this.getNonGokollabLocationDetailsByDomain(domain)
    } catch (error: any) {
      throw new LocationNotFoundError(error.message)
    }
  }

  async getNonGokollabLocationDetailsByDomain(domain: string) {
    const windowPortalSettings = (window as any)?.__preloadedApiData?.portalSettings
    if (windowPortalSettings && windowPortalSettings?.locationId) {
      return windowPortalSettings
    }
    return (await restAgent.ClientPortal.getLocationDetails(domain)).data
  }

  async getNonGokollabLocationDetailsByLocationId(locationId: string) {
    const windowPortalSettings = (window as any)?.__preloadedApiData?.portalSettings
    if (windowPortalSettings && windowPortalSettings?.locationId) {
      return windowPortalSettings
    }
    return (await restAgent.ClientPortal.getLocationDetailsByLocationId(locationId)).data
  }

  async fetchGroupDetails(route, router) {
    try {
      // Skip group initialization for no-communities route
      if (route.name === 'no-communities-mobile') {
        return
      }

      if (route?.params?.groupSlug === 'load-communities') {
        // First fetch all available groups
        await Promise.all([
          GroupState.getGroupByLocation({ limit: 15 }),
          GroupState.fetchPinnedGroups(),
          GroupsService.getRecentGroup()
            .then(data => {
              this._recentGroupSlug = data?.groupSlug || null
            })
            .catch(() => {
              this._recentGroupSlug = null
            }),
        ])

        // Prefer recent group for logged-in users, then pinned, then first group
        if (this._recentGroupSlug) {
          await initializeGroup(this._recentGroupSlug)
          await router.push({
            name: route.name || 'home',
            params: { groupSlug: this._recentGroupSlug },
          })
        } else if (GroupState.state.pinnedGroupList?.length) {
          await initializeGroup(GroupState.state.pinnedGroupList[0].slug)
          await router.push({
            name: route.name || 'home',
            params: { groupSlug: GroupState.state.pinnedGroupList[0].slug },
          })
        } else if (GroupState.state?.groupList?.length) {
          await initializeGroup(GroupState.state.groupList[0].slug)
          await router.push({
            name: route.name || 'home',
            params: { groupSlug: GroupState.state.groupList[0].slug },
          })
        } else {
          // No groups available, redirect to no-communities
          await router.push({
            name: 'no-communities-mobile',
          })
        }
        return
      }

      // Non-Mobile flow
      const promises: Promise<any>[] = [GroupState.getGroupByLocation({ limit: 15 })]

      if (!route.params?.groupSlug) {
        promises.push(
          GroupsService.getRecentGroup()
            .then(data => {
              this._recentGroupSlug = data?.groupSlug || null
            })
            .catch(() => {
              this._recentGroupSlug = null
            })
        )
      } else {
        useGlobalStore().setAppLoaded(true)
        promises.push(initializeGroup(route.params.groupSlug as string))
      }
      const result = await Promise.allSettled(promises)

      // if (
      //   !GroupState.state.groupList ||
      //   GroupState.state.groupList?.length === 0
      // ) {
      //   throw new NoGroupsFoundError('No groups found')
      // }

      //TODO: Open incorrect group slug page or no such page found page

      if (result[1]?.status === 'rejected' || !route?.params?.groupSlug) {
        if (isGokollab()) {
          if (isPwa()) {
            // Redirecting back to 'load-communities' for PWA to handle cases where a group becomes inactive,
            // which would otherwise redirect the user to the 'discovery' page, potentially causing an infinite redirect loop.
            window.location.href = '/load-communities'
          } else {
            window.location.href = '/discovery'
          }
        }

        const slugToLoad = this._recentGroupSlug || GroupState.state?.groupList[0]?.slug
        if (slugToLoad) {
          await initializeGroup(slugToLoad)
          await router.push({
            name: 'home',
            params: { groupSlug: slugToLoad },
          })
        }
      }

      if (route.name !== 'private-group' && route.name !== 'inactive-group') {
        this.state.readingMode = false
      }

      // this is to handle the edge case, where groupList is empty and no groupSlug is provided
      // then user is redirected to no-communities page
      if (GroupState.state?.groupList.length === 0 && !route?.params?.groupSlug) {
        redirectToNoCommunitiesPage(router)
      }
      return
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      useGlobalStore().setAppLoaded(true)
    }
  }

  async fetchUserDetails() {
    try {
      const appStore = useGlobalStore()
      const cookie = getSessionCookieData()
      if (!cookie) {
        appStore.setAuthenticated(false)
        return
      }

      if (this.state.locationId && cookie?.contactId) {
        appStore.setUserId(cookie?.contactId)
        const user = await UserState.getCurrentUser()
        if (user?.contactId) {
          //enableAnalytics(user.contactId)
          appStore.setAuthenticated(true)
          appStore.setUserId(user.contactId)
          updateColorTokens()
        }
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async fetchUserGroups() {
    try {
      GroupState.state.fetchingUserGroup = true
      const userGroups = await UsersService.getUserDetailsForCurrentGroup()
      GroupState.state.userGroup = userGroups
      GroupState.setRole((userGroups && userGroups?.role) || null)
      GroupState.state.userStatus = userGroups?.status ? userGroups.status || MemberStatus.ACTIVE : undefined
      if (UserState.state.currentUser && userGroups.contactId) {
        UserState.state.currentUser.contactId = userGroups.contactId
        useGlobalStore().setUserId(userGroups.contactId)
      }
      GroupState.state.fetchingUserGroup = false
      if (userGroups.membershipAnswers) {
        GroupState.state.userMembershipAnswers = userGroups.membershipAnswers
      }
    } catch (error) {
      console.error(error)
    }
  }

  async getGokollabLocationDetails() {
    return {
      _id: config.GOKOLLAB_LOCATION_ID,
      locationId: config.GOKOLLAB_LOCATION_ID,
      branding: {
        brandColor1: '',
      },
    }
  }
}

export const app = new App()
